<template>
    <div class="min-h-screen bg-gray-50">
        <AppHeader />

        <!-- Loading State -->
        <div v-if="loading" class="section-padding pt-24">
            <div class="container-custom">
                <div class="animate-pulse">
                    <div class="h-64 bg-gray-200 rounded-xl mb-8"></div>
                    <div class="flex gap-6">
                        <div class="w-24 h-24 bg-gray-200 rounded-2xl"></div>
                        <div class="flex-1">
                            <div class="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
                            <div class="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Product Content -->
        <div v-else-if="product">
            <!-- Hero Section -->
            <section class="relative overflow-hidden pt-20">
                <!-- Banner Background -->
                <div class="absolute inset-0 h-80 z-0">
                    <img v-if="product.banner_image_url" :src="product.banner_image_url" :alt="product.title"
                        class="w-full h-full object-cover" />
                    <div v-else class="w-full h-full bg-gradient-to-br from-primary-500 to-secondary-500"></div>
                    <div class="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-50"></div>
                </div>

                <div class="container-custom relative z-10 pt-40 pb-8">
                    <div class="bg-white rounded-3xl shadow-2xl p-6 lg:p-8">
                        <div class="flex flex-col lg:flex-row gap-6 lg:gap-8">
                            <!-- Product Icon -->
                            <div class="flex-shrink-0">
                                <div
                                    class="w-28 h-28 lg:w-32 lg:h-32 rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-primary-100 to-secondary-100 mx-auto lg:mx-0">
                                    <img v-if="product.icon_image_url" :src="product.icon_image_url"
                                        :alt="product.title" class="w-full h-full object-cover" />
                                    <div v-else
                                        class="w-full h-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center">
                                        <span class="text-4xl font-bold text-white">{{ product.title?.charAt(0)
                                            }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Product Info -->
                            <div class="flex-1 text-center lg:text-left">
                                <div class="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-3">
                                    <span v-if="product.category"
                                        class="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-semibold rounded-full">
                                        {{ product.category.name }}
                                    </span>
                                    <span v-if="product.featured"
                                        class="px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-semibold rounded-full flex items-center gap-1">
                                        <svg class="w-3 h-3 fill-current" viewBox="0 0 20 20">
                                            <path
                                                d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                        </svg>
                                        Featured
                                    </span>
                                </div>

                                <h1 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                                    {{ product.title }}
                                </h1>

                                <p class="text-primary-600 font-medium mb-4">
                                    {{ product.developer_name || 'Developer' }}
                                    <a v-if="product.developer_url" :href="product.developer_url" target="_blank"
                                        class="ml-1 text-gray-400 hover:text-primary-500">
                                        <svg class="w-4 h-4 inline" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14">
                                            </path>
                                        </svg>
                                    </a>
                                </p>

                                <!-- Stats Row -->
                                <div
                                    class="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6 mb-6">
                                    <!-- Rating -->
                                    <div class="flex items-center gap-2">
                                        <div class="flex items-center text-yellow-400">
                                            <svg v-for="i in 5" :key="i" class="w-5 h-5 fill-current"
                                                :class="i <= Math.round(product.rating || 0) ? 'text-yellow-400' : 'text-gray-300'"
                                                viewBox="0 0 20 20">
                                                <path
                                                    d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                            </svg>
                                        </div>
                                        <span class="text-lg font-bold text-gray-900">{{ product.rating || '0' }}</span>
                                        <span class="text-sm text-gray-500">({{ (parseInt(product.rating_count) || 0) + (product.testimonials?.length || 0) }}
                                            reviews)</span>
                                    </div>

                                    <span class="text-gray-300 hidden lg:block">|</span>

                                    <!-- Users -->
                                    <div class="flex items-center gap-2">
                                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                                            </path>
                                        </svg>
                                        <span class="font-semibold text-gray-700">{{ formatUsers(product.total_users)
                                            }}</span>
                                        <span class="text-gray-500">users</span>
                                    </div>

                                    <span class="text-gray-300 hidden lg:block">|</span>

                                    <!-- Platform -->
                                    <span
                                        :class="['px-3 py-1 rounded-full text-sm font-semibold', getPlatformClass(product.platform)]">
                                        {{ formatPlatform(product.platform) }}
                                    </span>
                                </div>

                                <!-- Action Buttons -->
                                <div class="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                                    <a v-if="product.download_url" :href="product.download_url" target="_blank"
                                        class="btn btn-primary px-8 py-3 text-lg shadow-lg shadow-primary-500/30">
                                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4">
                                            </path>
                                        </svg>
                                        {{ product.price == 0 ? 'Install Free' : 'Buy $' + product.price }}
                                    </a>
                                    <a v-if="product.website_url" :href="product.website_url" target="_blank"
                                        class="btn bg-white border-2 border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-600 px-6 py-3">
                                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9">
                                            </path>
                                        </svg>
                                        Visit Website
                                    </a>
                                    <a v-if="product.source_url" :href="product.source_url" target="_blank"
                                        class="btn bg-gray-900 text-white hover:bg-gray-800 px-6 py-3">
                                        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                            <path
                                                d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                        </svg>
                                        Source Code
                                    </a>
                                </div>
                            </div>

                            <!-- Version Info (Desktop) -->
                            <div class="hidden lg:block flex-shrink-0 w-48 border-l border-gray-200 pl-6">
                                <div class="space-y-4">
                                    <div>
                                        <p class="text-xs text-gray-500 uppercase tracking-wide">Version</p>
                                        <p class="font-semibold text-gray-900">{{ product.version || 'N/A' }}</p>
                                    </div>
                                    <div v-if="product.last_updated">
                                        <p class="text-xs text-gray-500 uppercase tracking-wide">Updated</p>
                                        <p class="font-semibold text-gray-900">{{ formatDate(product.last_updated) }}
                                        </p>
                                    </div>
                                    <div v-if="product.release_date">
                                        <p class="text-xs text-gray-500 uppercase tracking-wide">Released</p>
                                        <p class="font-semibold text-gray-900">{{ formatDate(product.release_date) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Screenshots Gallery -->
            <section v-if="product.screenshots && product.screenshots.length > 0" class="py-8 bg-white">
                <div class="container-custom">
                    <h2 class="font-heading text-xl font-bold text-gray-900 mb-4">Screenshots</h2>
                    <div class="flex gap-4 overflow-x-auto pb-6 scrollbar-hide py-2">
                        <div v-for="screenshot in product.screenshots" :key="screenshot.id"
                            class="flex-shrink-0 w-48 lg:w-56">
                            <div class="aspect-[9/16] rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group cursor-pointer relative"
                                @click="openLightbox(screenshot)">
                                <img :src="screenshot.image_url" :alt="screenshot.caption || 'Screenshot'"
                                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                <div class="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>
                            </div>
                            <p v-if="screenshot.caption" class="text-xs font-medium text-gray-500 mt-3 text-center truncate px-2">{{
                                screenshot.caption }}</p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Tabs Section -->
            <section class="py-8 bg-gray-50">
                <div class="container-custom">
                    <!-- Tab Navigation -->
                    <div class="flex items-center gap-1 p-1 bg-white rounded-2xl shadow-sm mb-8 overflow-x-auto">
                        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id" :class="[
                            'flex-shrink-0 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap',
                            activeTab === tab.id
                                ? 'bg-primary-600 text-white shadow-lg'
                                : 'text-gray-600 hover:bg-gray-100'
                        ]">
                            {{ tab.label }}
                        </button>
                    </div>

                    <!-- Tab Content -->
                    <div class="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                        <!-- About Tab -->
                        <div v-show="activeTab === 'about'">
                            <h3 class="font-heading text-2xl font-bold text-gray-900 mb-4">About This Product</h3>
                            <div class="prose prose-lg max-w-none text-gray-600"
                                v-html="formatDescription(product.long_description || product.short_description)"></div>

                            <!-- Features List -->
                            <div v-if="product.features && product.features.length > 0" class="mt-8">
                                <h4 class="font-heading text-xl font-bold text-gray-900 mb-4">Key Features</h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div v-for="(feature, index) in product.features" :key="index"
                                        class="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                                        <div
                                            class="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                                            <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M5 13l4 4L19 7"></path>
                                            </svg>
                                        </div>
                                        <span class="text-gray-700">{{ feature }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Installation Tab -->
                        <div v-show="activeTab === 'install'">
                            <h3 class="font-heading text-2xl font-bold text-gray-900 mb-4">Installation Guide</h3>

                            <div v-if="product.install_instructions && product.install_instructions.length > 0">
                                <div class="space-y-6">
                                    <div v-for="(step, index) in product.install_instructions" :key="index"
                                        class="flex gap-4">
                                        <div
                                            class="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                                            {{ index + 1 }}
                                        </div>
                                        <div class="flex-1 pt-1">
                                            <p class="text-gray-700">{{ step }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else class="text-center py-12">
                                <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                    </path>
                                </svg>
                                <p class="text-gray-500">No installation instructions available.</p>
                            </div>

                            <!-- Requirements -->
                            <div v-if="product.requirements && product.requirements.length > 0"
                                class="mt-8 p-6 bg-gray-50 rounded-xl">
                                <h4 class="font-heading text-lg font-bold text-gray-900 mb-3">System Requirements</h4>
                                <ul class="space-y-2">
                                    <li v-for="(req, index) in product.requirements" :key="index"
                                        class="flex items-center gap-2 text-gray-600">
                                        <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        {{ req }}
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <!-- Technical Features Tab -->
                        <div v-show="activeTab === 'features'">
                            <h3 class="font-heading text-2xl font-bold text-gray-900 mb-6">Technical Features</h3>

                            <div v-if="product.technical_features && product.technical_features.length > 0"
                                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div v-for="feature in product.technical_features" :key="feature.id"
                                    class="p-6 bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl hover:shadow-lg transition-shadow">
                                    <div
                                        class="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                                        <span v-if="feature.icon" class="text-2xl">{{ feature.icon }}</span>
                                        <svg v-else class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                        </svg>
                                    </div>
                                    <h4 class="font-bold text-gray-900 mb-2">{{ feature.title }}</h4>
                                    <p class="text-sm text-gray-600">{{ feature.description }}</p>
                                </div>
                            </div>

                            <div v-else class="text-center py-12">
                                <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z">
                                    </path>
                                </svg>
                                <p class="text-gray-500">No technical features listed.</p>
                            </div>
                        </div>

                        <!-- Download/Share Tab -->
                        <div v-show="activeTab === 'download'">
                            <h3 class="font-heading text-2xl font-bold text-gray-900 mb-6">Download & Share</h3>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <!-- Download Options -->
                                <div class="space-y-4">
                                    <h4 class="font-bold text-gray-900 mb-4">Download Options</h4>

                                    <a v-if="product.download_url" :href="product.download_url" target="_blank"
                                        class="flex items-center gap-4 p-4 bg-primary-50 border-2 border-primary-200 rounded-xl hover:bg-primary-100 transition-colors">
                                        <div
                                            class="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
                                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4">
                                                </path>
                                            </svg>
                                        </div>
                                        <div class="flex-1">
                                            <p class="font-bold text-primary-700">Download Now</p>
                                            <p class="text-sm text-primary-600">Get the latest version</p>
                                        </div>
                                        <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </a>

                                    <a v-if="product.website_url" :href="product.website_url" target="_blank"
                                        class="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors">
                                        <div class="w-12 h-12 bg-gray-600 rounded-xl flex items-center justify-center">
                                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9">
                                                </path>
                                            </svg>
                                        </div>
                                        <div class="flex-1">
                                            <p class="font-bold text-gray-700">Visit Website</p>
                                            <p class="text-sm text-gray-500">Learn more on official site</p>
                                        </div>
                                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14">
                                            </path>
                                        </svg>
                                    </a>
                                </div>

                                <!-- Share Options -->
                                <div>
                                    <h4 class="font-bold text-gray-900 mb-4">Share This Product</h4>
                                    <div class="flex gap-3">
                                        <button @click="shareOnTwitter"
                                            class="w-12 h-12 bg-[#1DA1F2] rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity">
                                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                            </svg>
                                        </button>
                                        <button @click="shareOnFacebook"
                                            class="w-12 h-12 bg-[#1877F2] rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity">
                                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                            </svg>
                                        </button>
                                        <button @click="shareOnLinkedIn"
                                            class="w-12 h-12 bg-[#0A66C2] rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity">
                                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                            </svg>
                                        </button>
                                        <button @click="copyLink"
                                            class="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-colors">
                                            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z">
                                                </path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Testimonials Section -->
            <section class="py-12 bg-white">
                <div class="container-custom">
                    <div class="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
                        <div class="text-center md:text-left">
                            <h2 class="font-heading text-3xl font-bold text-gray-900 mb-2">What Users Say</h2>
                            <p class="text-gray-600">Reviews from happy customers</p>
                        </div>
                        <button @click="showTestimonialForm = !showTestimonialForm" 
                            class="btn btn-primary px-6 py-2 shadow-lg shadow-primary-500/20">
                            {{ showTestimonialForm ? 'Cancel' : 'Write a Review' }}
                        </button>
                    </div>

                    <!-- Testimonial Submission Form -->
                    <div v-if="showTestimonialForm" class="mb-12 bg-gray-50 rounded-3xl p-6 lg:p-8 border border-gray-100 shadow-sm transition-all duration-300">
                        <h3 class="font-heading text-xl font-bold text-gray-900 mb-6">Share Your Experience</h3>
                        <form @submit.prevent="submitTestimonial" class="space-y-6">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                                    <input v-model="testimonialForm.name" type="text" required
                                        class="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                                    <div class="flex gap-1 py-2">
                                        <button v-for="star in 5" :key="star" type="button"
                                            @click="testimonialForm.rating = star"
                                            class="text-2xl transition-transform hover:scale-110">
                                            <span :class="star <= testimonialForm.rating ? 'text-yellow-400' : 'text-gray-300'">★</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Your Review *</label>
                                <textarea v-model="testimonialForm.content" rows="4" required
                                    class="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Tell us what you like about this product..."></textarea>
                            </div>
                            <div class="flex justify-end">
                                <button type="submit" :disabled="submittingTestimonial" class="btn btn-primary px-10 py-3 shadow-xl shadow-primary-500/30">
                                    <span v-if="submittingTestimonial">Submitting...</span>
                                    <span v-else>Submit Review</span>
                                </button>
                            </div>
                        </form>
                    </div>

                    <div v-if="product.testimonials && product.testimonials.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="testimonial in product.testimonials" :key="testimonial.id"
                            class="bg-gray-50 rounded-2xl p-6 border border-transparent hover:border-primary-100 hover:bg-white hover:shadow-xl transition-all duration-300 group">
                            <div class="flex items-center gap-1 mb-4">
                                <svg v-for="i in 5" :key="i" class="w-4 h-4 fill-current"
                                    :class="i <= testimonial.rating ? 'text-yellow-400' : 'text-gray-300'"
                                    viewBox="0 0 20 20">
                                    <path
                                        d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                </svg>
                            </div>
                            <p class="text-gray-600 mb-6 italic leading-relaxed">"{{ testimonial.content }}"</p>
                            <div class="flex items-center gap-3">
                                <div class="w-12 h-12 bg-gradient-to-br from-primary-500/10 to-secondary-500/10 rounded-2xl flex items-center justify-center group-hover:from-primary-500 group-hover:to-secondary-500 transition-all duration-300">
                                    <span class="font-bold text-primary-600 group-hover:text-white">{{ testimonial.name?.charAt(0) }}</span>
                                </div>
                                <div>
                                    <p class="font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{{ testimonial.name }}</p>
                                    <p class="text-xs text-gray-500 uppercase tracking-wider font-semibold">{{ testimonial.role || 'User' }} {{ testimonial.company ? '• ' + testimonial.company : '' }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center py-20 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                        <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                        </svg>
                        <p class="text-gray-500">No reviews yet. Be the first to share your experience!</p>
                    </div>
                </div>
            </section>

            <!-- Related Products -->
            <section v-if="relatedProducts.length > 0" class="py-12 bg-gray-50">
                <div class="container-custom">
                    <h2 class="font-heading text-2xl font-bold text-gray-900 mb-6">You May Also Like</h2>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <router-link v-for="relatedProduct in relatedProducts" :key="relatedProduct.id"
                            :to="{ name: 'ProductDetail', params: { id: relatedProduct.uid } }"
                            class="bg-white rounded-2xl p-4 shadow-sm hover:shadow-lg transition-all group">
                            <div class="flex items-center gap-4">
                                <div class="w-16 h-16 rounded-xl overflow-hidden bg-gray-100">
                                    <img v-if="relatedProduct.icon_image_url" :src="relatedProduct.icon_image_url"
                                        :alt="relatedProduct.title" class="w-full h-full object-cover" />
                                    <div v-else
                                        class="w-full h-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center">
                                        <span class="text-lg font-bold text-white">{{ relatedProduct.title?.charAt(0)
                                            }}</span>
                                    </div>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <h3
                                        class="font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                                        {{ relatedProduct.title }}</h3>
                                    <p class="text-sm text-gray-500 truncate">{{ relatedProduct.developer_name ||
                                        'Developer' }}</p>
                                    <div class="flex items-center gap-1 mt-1">
                                        <svg class="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                                            <path
                                                d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                        </svg>
                                        <span class="text-sm text-gray-600">{{ relatedProduct.rating || '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </router-link>
                    </div>
                </div>
            </section>
        </div>

        <!-- Error State -->
        <div v-else class="section-padding pt-24">
            <div class="container-custom text-center">
                <div class="max-w-md mx-auto">
                    <svg class="w-24 h-24 text-gray-300 mx-auto mb-6" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
                        </path>
                    </svg>
                    <h2 class="font-heading text-3xl font-bold text-gray-900 mb-4">Product Not Found</h2>
                    <p class="text-gray-600 mb-8">The product you're looking for doesn't exist or has been removed.</p>
                    <router-link to="/products" class="btn btn-primary">
                        Browse Products
                    </router-link>
                </div>
            </div>
        </div>

        <AppFooter />

        <!-- Enhanced Screenshot Lightbox -->
        <Teleport to="body">
            <Transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="opacity-0 pb-10"
                enter-to-class="opacity-100 pb-0"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0 scale-95"
            >
                <div v-if="lightboxImage" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/95 backdrop-blur-sm">
                    <!-- Close Button -->
                    <button @click="closeLightbox" class="absolute top-6 right-6 z-[120] p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors">
                        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>

                    <!-- Navigation Controls -->
                    <div class="absolute inset-y-0 left-0 flex items-center p-4 z-[110]">
                        <button @click="prevScreenshot" class="p-4 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                            </svg>
                        </button>
                    </div>
                    <div class="absolute inset-y-0 right-0 flex items-center p-4 z-[110]">
                        <button @click="nextScreenshot" class="p-4 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    </div>

                    <!-- Zoom Controls -->
                    <div class="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 z-[110]">
                        <button @click="zoomOut" class="text-white hover:text-primary-400 p-1">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"></path>
                            </svg>
                        </button>
                        <span class="text-white font-medium min-w-[3rem] text-center">{{ Math.round(zoomLevel * 100) }}%</span>
                        <button @click="zoomIn" class="text-white hover:text-primary-400 p-1">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                            </svg>
                        </button>
                        <button @click="resetZoom" class="text-white hover:text-primary-400 p-1 ml-2 border-l border-white/20 pl-4">
                            Reset
                        </button>
                    </div>

                    <!-- Image Container -->
                    <div class="relative w-full h-full flex items-center justify-center overflow-hidden" @click.self="closeLightbox">
                        <div class="relative w-full h-full flex items-center justify-center p-4 lg:p-12 overflow-auto scrollbar-hide">
                            <img 
                                :src="lightboxImage" 
                                class="max-w-full max-h-full object-contain transition-transform duration-300 ease-out shadow-2xl rounded-lg lg:rounded-2xl"
                                :style="{ transform: `scale(${zoomLevel})` }"
                                @click.stop
                            />
                        </div>
                    </div>

                    <!-- Caption -->
                    <div class="absolute bottom-24 left-1/2 -translate-x-1/2 text-center">
                        <p class="text-white text-lg font-medium drop-shadow-lg">{{ currentScreenshot?.caption }}</p>
                        <p class="text-white/60 text-sm mt-1">{{ currentScreenshotIndex + 1 }} / {{ product.screenshots.length }}</p>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useProductStore } from '@/stores/products'
import { useUIStore } from '@/stores/ui'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

const route = useRoute()
const productStore = useProductStore()
const uiStore = useUIStore()

const activeTab = ref('about')
const lightboxImage = ref(null)
const showTestimonialForm = ref(false)
const submittingTestimonial = ref(false)
const testimonialForm = ref({
    name: '',
    role: 'Customer',
    content: '',
    rating: 5
})

const currentScreenshotIndex = ref(0)
const zoomLevel = ref(1)

const currentScreenshot = computed(() => {
    if (!product.value || !product.value.screenshots) return null
    return product.value.screenshots[currentScreenshotIndex.value]
})

const tabs = [
    { id: 'about', label: 'About' },
    { id: 'install', label: 'Installation' },
    { id: 'features', label: 'Technical Features' },
    { id: 'download', label: 'Download & Share' }
]

const loading = computed(() => productStore.loading)
const product = computed(() => productStore.currentProduct)

const relatedProducts = computed(() => {
    if (!product.value) return []
    return productStore.products
        .filter(p => p.id !== product.value.id)
        .slice(0, 4)
})

onMounted(async () => {
    await loadProduct()
    if (productStore.products.length === 0) {
        await productStore.fetchProducts()
    }
    window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
})

watch(() => route.params.id, async (newId) => {
    if (newId) {
        await loadProduct()
    }
})

const loadProduct = async () => {
    const id = route.params.id
    await productStore.fetchProduct(id)
}

const formatUsers = (count) => {
    if (!count) return '0'
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
    return count.toString()
}

const formatPlatform = (platform) => {
    const platforms = {
        'mobile': 'Mobile',
        'web': 'Web App',
        'desktop': 'Desktop',
        'cross-platform': 'Cross-Platform'
    }
    return platforms[platform] || platform
}

const getPlatformClass = (platform) => {
    const classes = {
        'mobile': 'bg-blue-100 text-blue-700',
        'web': 'bg-green-100 text-green-700',
        'desktop': 'bg-purple-100 text-purple-700',
        'cross-platform': 'bg-orange-100 text-orange-700'
    }
    return classes[platform] || 'bg-gray-100 text-gray-700'
}

const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
}

const formatDescription = (text) => {
    if (!text) return ''
    return text.replace(/\n/g, '<br>')
}

const openLightbox = (screenshot) => {
    const index = product.value.screenshots.findIndex(s => s.id === screenshot.id)
    if (index !== -1) {
        currentScreenshotIndex.value = index
        lightboxImage.value = screenshot.image_url
        zoomLevel.value = 1
        document.body.style.overflow = 'hidden'
    }
}

const closeLightbox = () => {
    lightboxImage.value = null
    zoomLevel.value = 1
    document.body.style.overflow = ''
}

const nextScreenshot = () => {
    if (!product.value || !product.value.screenshots) return
    currentScreenshotIndex.value = (currentScreenshotIndex.value + 1) % product.value.screenshots.length
    lightboxImage.value = product.value.screenshots[currentScreenshotIndex.value].image_url
    zoomLevel.value = 1
}

const prevScreenshot = () => {
    if (!product.value || !product.value.screenshots) return
    currentScreenshotIndex.value = (currentScreenshotIndex.value - 1 + product.value.screenshots.length) % product.value.screenshots.length
    lightboxImage.value = product.value.screenshots[currentScreenshotIndex.value].image_url
    zoomLevel.value = 1
}

const zoomIn = () => {
    if (zoomLevel.value < 4) zoomLevel.value += 0.25
}

const zoomOut = () => {
    if (zoomLevel.value > 0.5) zoomLevel.value -= 0.25
}

const resetZoom = () => {
    zoomLevel.value = 1
}

const handleKeydown = (e) => {
    if (!lightboxImage.value) return
    
    if (e.key === 'Escape') closeLightbox()
    if (e.key === 'ArrowRight') nextScreenshot()
    if (e.key === 'ArrowLeft') prevScreenshot()
    if (e.key === '+' || e.key === '=') zoomIn()
    if (e.key === '-' || e.key === '_') zoomOut()
}

const shareOnTwitter = () => {
    const url = window.location.href
    const text = `Check out ${product.value?.title}!`
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank')
}

const shareOnFacebook = () => {
    const url = window.location.href
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
}

const shareOnLinkedIn = () => {
    const url = window.location.href
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank')
}

const copyLink = async () => {
    try {
        await navigator.clipboard.writeText(window.location.href)
        alert('Link copied to clipboard!')
    } catch (err) {
        console.error('Failed to copy link:', err)
    }
}

const submitTestimonial = async () => {
    if (!testimonialForm.value.name || !testimonialForm.value.content || !product.value) return

    try {
        submittingTestimonial.value = true
        loading.value = true
        const response = await productStore.addTestimonial(product.value.id, testimonialForm.value)
        
        if (response) {
            uiStore.showNotification({
                type: 'success',
                title: 'Review Submitted',
                message: 'Thank you for your review! It will be visible after moderation.'
            })
            // Reset form
            testimonialForm.value = { name: '', role: 'Customer', content: '', rating: 5 }
            showTestimonialForm.value = false
            // Refresh product to see the new testimonial (if it's automatically active)
            await loadProduct()
        }
    } catch (error) {
        console.error('Error submitting testimonial:', error)
    } finally {
        submittingTestimonial.value = false
        loading.value = false
    }
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.prose :deep(p) {
    margin-bottom: 1rem;
}
</style>
